import type { FileConfigDTO, FileConfigFields } from '@today/types'
import { Button, Switch } from '@today/ui-primitives'
import { useState } from 'react'
import { FILE_TYPE_LABELS } from '../file-exchange-file-list/file-type-labels'
import { type FieldErrors, GroupError, NumberInput, TextInput } from './form-fields'
import { RetrievalFields } from './retrieval-fields'
import { ScheduleFields } from './schedule-fields'

/**
 * Props for {@link FileExchangeFileForm}.
 *
 * @property file - The stored file; the form is seeded from it once (key the form on `version` to re-seed).
 * @property readOnly - The manifest grants READ: every field is disabled and no save is offered.
 * @property isBusy - A save is in flight; saving is disabled.
 * @property fieldErrors - API field path → message, shown next to that field.
 * @property error - A refusal to announce for the whole form (conflict, failure).
 * @property onSave - Reports the four configuration fields to store.
 * @property onReload - When given, the error offers a Reload action.
 */
export interface FileExchangeFileFormProps {
	file: FileConfigDTO
	readOnly?: boolean
	isBusy?: boolean
	fieldErrors?: FieldErrors
	error?: string | null
	onSave: (fields: FileConfigFields) => void
	onReload?: () => void
}

/** The editable state: the import definition is kept as two inputs until it is submitted. */
interface Draft extends Omit<FileConfigFields, 'importDefinition'> {
	definitionKey: string
	definitionVersion: number
}

const draftOf = (file: FileConfigDTO): Draft => ({
	enabled: file.enabled,
	definitionKey: file.importDefinition?.key ?? '',
	definitionVersion: file.importDefinition?.version ?? 1,
	retrieval: structuredClone(file.retrieval),
	schedule: file.schedule ? structuredClone(file.schedule) : null,
})

/** An emptied key means "no import definition bound". */
const submissionOf = ({ definitionKey, definitionVersion, ...draft }: Draft): FileConfigFields => ({
	enabled: draft.enabled,
	importDefinition: definitionKey.trim() === '' ? null : { key: definitionKey.trim(), version: definitionVersion },
	retrieval: draft.retrieval,
	schedule: draft.schedule,
})

const NO_ERRORS: FieldErrors = {}

type BasicsProps = {
	draft: Draft
	readOnly: boolean
	errors: FieldErrors
	update: (patch: Partial<Draft>) => void
}

/** Whether the file is exchanged at all, and the import definition that reads it. */
function FileBasics({ draft, readOnly, errors, update }: BasicsProps) {
	return (
		<>
			<div className="flex flex-col gap-1">
				<Switch isSelected={draft.enabled} isDisabled={readOnly} onChange={(enabled) => update({ enabled })}>
					Enabled
				</Switch>
				<GroupError path="enabled" errors={errors} />
			</div>
			<fieldset className="flex flex-col gap-3">
				<legend className="text-ds-sm font-semibold text-ds-text">Import definition</legend>
				<GroupError path="importDefinition" errors={errors} />
				<TextInput
					label="Import definition key"
					path="importDefinition.key"
					errors={errors}
					value={draft.definitionKey}
					onChange={(definitionKey) => update({ definitionKey })}
				/>
				<NumberInput
					label="Import definition version"
					path="importDefinition.version"
					errors={errors}
					value={draft.definitionVersion}
					onChange={(definitionVersion) => update({ definitionVersion })}
				/>
			</fieldset>
		</>
	)
}

function FormAlert({ error, onReload }: { error: string; onReload?: () => void }) {
	return (
		<div role="alert" className="flex items-center gap-3 text-ds-sm text-ds-danger">
			<span>{error}</span>
			{onReload ? (
				<Button size="sm" variant="muted" onPress={onReload}>
					Reload
				</Button>
			) : null}
		</div>
	)
}

/**
 * One logical file's configuration (TO-391 REQ-015): enabled, the bound import
 * definition, how the file arrives and when it is expected. One save replaces
 * the whole file.
 *
 * Pure: the page's hook sends the request; every rule is the server's, and a
 * refusal arrives here as `fieldErrors` (by API path) or `error`.
 */
export function FileExchangeFileForm({
	file,
	readOnly = false,
	isBusy = false,
	fieldErrors = NO_ERRORS,
	error,
	onSave,
	onReload,
}: FileExchangeFileFormProps) {
	const [draft, setDraft] = useState(() => draftOf(file))
	const update = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }))
	const title = FILE_TYPE_LABELS[file.fileType]

	return (
		<section className="flex flex-col gap-6" aria-label={`${title} file settings`}>
			<h3 className="text-ds-base font-semibold text-ds-text">{title} file</h3>
			{error ? <FormAlert error={error} onReload={onReload} /> : null}
			<fieldset disabled={readOnly} className="flex flex-col gap-6">
				<FileBasics draft={draft} readOnly={readOnly} errors={fieldErrors} update={update} />
				<RetrievalFields
					retrieval={draft.retrieval}
					errors={fieldErrors}
					onChange={(retrieval) => update({ retrieval })}
				/>
				<ScheduleFields schedule={draft.schedule} errors={fieldErrors} onChange={(schedule) => update({ schedule })} />
			</fieldset>
			{readOnly ? null : (
				<div>
					<Button variant="primary" isDisabled={isBusy} onPress={() => onSave(submissionOf(draft))}>
						Save file settings
					</Button>
				</div>
			)}
		</section>
	)
}
